import { useChatStore as useChatStoreCore } from '../chatStore'
import {
  ChatStateManager,
  ChatTransformer,
} from '../../services'
import { isDialogChat, isGroupLikeChat } from '../../types/ChatMessage'
import { mergeEventChatFields } from '../../constants/chat'
import type { ChatContact, ChatContactWithChat, ChatOut } from '../../types'
import { defineChatViewActions } from './actionsContext'
import { applyCoreLastMessageToChatOut } from './listHelpers'
import {
  isSameAvatarAsset,
  normalizeAvatarUrl,
  preferStableAvatar,
} from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import {
  avatarDebugLog,
  debugConsoleError,
  debugConsoleWarn,
  logDialogProfile,
  summarizeAvatarUrlForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import {
  getMatrixRoomAvatarUrl,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { getMatrixPeerId } from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import { resolveMatrixSenderProfile } from '@/modules/chat-matrix-fast/services/matrixProfileCache'
import { resolveDialogPeerAvatar } from '@/modules/chat-matrix-fast/services/dialogAvatar'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'

export const activeChatActions = defineChatViewActions({
  updateActiveChat(chat: IChat, userId: number | string): void {
    if (!chat) {
      debugConsoleError('Cannot update active chat: chat is null')

      return
    }

    let contact: ChatContactWithChat | undefined
        = this.chatsContacts.find(c => c.id === userId)

    if (!contact) {
      const transformedChats = ChatTransformer.transformChatsToContacts(
        [chat],
        this.profileUser?.id,
      )

      if (transformedChats.length > 0) {
        contact = transformedChats[0]

        const existingContactIndex = this.chatsContacts.findIndex(c => c.id === userId || c.id === contact!.id)

        if (existingContactIndex === -1) {
          this.chatsContacts.push(contact)
        }
      }
    }

    if (!contact) {
      return
    }

    try {
      const chatStore = useChatStoreCore()

      const chatInStore = chatStore.chats.find(c => c.id === chat.id)
      const previousActiveChat = this.activeChat?.chat?.id === chat.id
        ? (this.activeChat.chat as IChat)
        : undefined
      const currentChatFallback = chatStore.currentChat?.id === chat.id
        ? chatStore.currentChat
        : undefined
      const chatToUse = mergeEventChatFields(
        (chatInStore || chat) as IChat,
        chat,
        previousActiveChat,
        currentChatFallback,
      )

      let transformedChat: ChatOut
      try {
        transformedChat = mergeEventChatFields(
          ChatTransformer.transformChatToChatOut(
            chatToUse,
            [],
          ) as IChat,
          chatToUse,
          previousActiveChat,
          currentChatFallback,
        ) as ChatOut
      } catch (error) {
        debugConsoleError('[chatViewStore] Ошибка при трансформации чата в updateActiveChat:', error)

        transformedChat = chatToUse as ChatOut
      }

      if (chatInStore?.last_message) {
        applyCoreLastMessageToChatOut(transformedChat, chatInStore.last_message)
      } else if (
        (chatStore.currentChat?.id === chat.id || chatStore.activeTopic)
          && chatStore.messages.length > 0
      ) {
        applyCoreLastMessageToChatOut(
          transformedChat,
          chatStore.messages[chatStore.messages.length - 1],
        )
      }

      transformedChat.messages = []

      const transformedChats = ChatTransformer.transformChatsToContacts(
        [chatToUse],
        this.profileUser?.id,
      )

      const freshContact = transformedChats.length > 0 ? transformedChats[0] : contact

      const enrichedContact = ChatStateManager.enrichContactWithAvatar(
        freshContact,
        chatToUse,
        this.profileUser?.id,
      )

      // Не подменяем уже показанный аватар новым signed URL — иначе мигает <img>.
      const previousAvatar
          = (this.activeChat?.chat?.id === chat.id || String(this.activeChat?.contact?.id) === String(userId)
            ? this.activeChat?.contact?.avatar
            : undefined)
          || this.chatsContacts.find(
            c => c.chat?.id === chat.id || String(c.id) === String(userId),
          )?.avatar

      const stableContactAvatar = preferStableAvatar(previousAvatar, enrichedContact.avatar)
          || enrichedContact.avatar
          || previousAvatar
          || ''

      const contactForActive = {
        ...enrichedContact,
        avatar: stableContactAvatar,
      }

      if (
        isGroupLikeChat(transformedChat)
          && 'icon' in transformedChat
      ) {
        const previousIcon = this.activeChat?.chat?.id === chat.id
            && this.activeChat.chat
            && 'icon' in this.activeChat.chat
          ? this.activeChat.chat.icon
          : undefined
        const sidebarIcon = this.chatsContacts.find(c => c.chat?.id === chat.id)?.chat
        const previousSidebarIcon = sidebarIcon && 'icon' in sidebarIcon ? sidebarIcon.icon : undefined
        const iconBefore = transformedChat.icon
        transformedChat.icon = preferStableAvatar(
          previousIcon || previousSidebarIcon || contactForActive.avatar,
          transformedChat.icon,
        ) || transformedChat.icon
        avatarDebugLog('activeChat · group icon', {
          chatId: chat.id,
          title: chat.title,
          switchingAwayFrom: this.activeChat?.chat?.id !== chat.id
            ? this.activeChat?.chat?.id
            : null,
          previousAvatar: summarizeAvatarUrlForLog(previousAvatar),
          enriched: summarizeAvatarUrlForLog(enrichedContact.avatar),
          stableContact: summarizeAvatarUrlForLog(stableContactAvatar),
          iconBefore: summarizeAvatarUrlForLog(iconBefore),
          iconAfter: summarizeAvatarUrlForLog(
            typeof transformedChat.icon === 'string' ? transformedChat.icon : null,
          ),
          previousIcon: summarizeAvatarUrlForLog(
            typeof previousIcon === 'string' ? previousIcon : null,
          ),
          previousSidebarIcon: summarizeAvatarUrlForLog(
            typeof previousSidebarIcon === 'string' ? previousSidebarIcon : null,
          ),
        })
      }

      this.activeChat = {
        chat: transformedChat,
        contact: contactForActive,
      }

      if (isDialogChat(transformedChat)) {
        logDialogProfile('updateActiveChat', {
          chatId: chat.id,
          userId,
          matrix_room_id: chatToUse.matrix_room_id ?? null,
          matrix_peer_id: chatToUse.matrix_peer_id ?? null,
          chatIcon: chatToUse.icon ?? null,
          chatTitle: chatToUse.title ?? null,
          contactFullName: contactForActive.fullName,
          contactAvatarBefore: freshContact.avatar || null,
          contactAvatarAfter: contactForActive.avatar || null,
          membersCount: chatToUse.members?.length ?? 0,
          members: (chatToUse.members ?? []).map(member => ({
            user: member.user ?? null,
            user_uuid: member.user_uuid ?? null,
            avatar: member.avatar ?? member.photo ?? null,
            full_name: member.full_name ?? null,
          })),
          profileUserId: this.profileUser?.id ?? null,
          firstMessagePeerAvatar: chatStore.messages.find(
            message => message.created_by?.id && String(message.created_by.id) !== String(this.profileUser?.id),
          )?.created_by?.avatar ?? null,
        })
      }

      this.syncGroupRoomAvatar(chat.id, userId, chatToUse, transformedChat)
      this.syncDialogPeerAvatar(chat.id, userId, chatToUse)

      // Prefer fresher core preview on open — else sidebar lags until timeline tick.
      const activeContactIndex = this.chatsContacts.findIndex(c => c.chat?.id === chat.id)
      if (activeContactIndex !== -1) {
        const previousContact = this.chatsContacts[activeContactIndex]
        const coreLastMessage = chatInStore?.last_message
        const previousLastMessage = previousContact.chat?.lastMessage
        const previousTime = Date.parse(previousLastMessage?.time ?? '')
        const coreTime = Date.parse(coreLastMessage?.created_at ?? '')
        // Same last_message with newer timestamp must not reorder sidebar on open.
        const useCorePreview = Boolean(
          coreLastMessage
            && (
              !previousLastMessage
              || (
                !Number.isNaN(coreTime)
                && (Number.isNaN(previousTime) || coreTime > previousTime)
              )
            ),
        )
        const preservedLastMessage = useCorePreview && coreLastMessage
          ? undefined
          : previousLastMessage
        const stableAvatar = preferStableAvatar(previousContact.avatar, contactForActive.avatar)
        const previousIcon = previousContact.chat && 'icon' in previousContact.chat
          ? previousContact.chat.icon
          : undefined
        const enrichedIcon = contactForActive.chat && 'icon' in contactForActive.chat
          ? contactForActive.chat.icon
          : undefined
        const stableIcon = preferStableAvatar(previousIcon, enrichedIcon)

        const nextContact = {
          ...previousContact,
          ...contactForActive,
          avatar: stableAvatar || previousContact.avatar,
          chat: {
            ...contactForActive.chat,
            ...(stableIcon != null && isGroupLikeChat(contactForActive.chat)
              ? { icon: stableIcon }
              : {}),
            unseenMsgs: previousContact.chat?.unseenMsgs ?? 0,
          },
        }

        if (preservedLastMessage) {
          nextContact.chat = {
            ...nextContact.chat,
            lastMessage: preservedLastMessage,
          }
        } else if (coreLastMessage) {
          applyCoreLastMessageToChatOut(nextContact.chat, coreLastMessage)
        }

        this.chatsContacts[activeContactIndex] = nextContact
      }
    } catch (error) {
      debugConsoleError('[chatViewStore] Ошибка в updateActiveChat:', error)
    }
  },

  syncDialogPeerAvatar(
    chatId: number | string,
    userId: number | string,
    sourceChat: IChat,
  ): void {
    if (!isDialogChat(sourceChat as ChatOut)) {
      return
    }

    const applyAvatar = (avatarUrl: string) => {
      if (this.activeChat?.chat?.id !== chatId) {
        return
      }

      const currentAvatar = this.activeChat?.contact?.avatar
          || this.chatsContacts.find(c => c.id === userId)?.avatar
      const stableAvatar = preferStableAvatar(currentAvatar, avatarUrl) || avatarUrl

      if (currentAvatar && isSameAvatarAsset(currentAvatar, stableAvatar)) {
        return
      }

      if (this.activeChat?.contact) {
        this.activeChat.contact.avatar = stableAvatar
      }

      const contactIndex = this.chatsContacts.findIndex(c => c.id === userId)
      if (contactIndex !== -1) {
        this.chatsContacts[contactIndex] = {
          ...this.chatsContacts[contactIndex],
          avatar: stableAvatar,
        }
      }

      const chatStore = useChatStoreCore()
      const chatIndex = chatStore.chats.findIndex(c => c.id === chatId)
      if (chatIndex !== -1) {
        chatStore.chats[chatIndex] = {
          ...chatStore.chats[chatIndex],
          icon: preferStableAvatar(chatStore.chats[chatIndex].icon, stableAvatar) || stableAvatar,
        }
      }

      logDialogProfile('syncDialogPeerAvatar · avatar applied', {
        chatId,
        appliedAvatar: stableAvatar,
      })
      // Не вызываем syncViewWithCore — полный sync+sort при смене аватара
    }

    const hintedAvatar = normalizeAvatarUrl(sourceChat.icon)
        || normalizeAvatarUrl(sourceChat.display_avatar_url)
        || normalizeAvatarUrl(this.activeChat?.contact?.avatar)
    if (hintedAvatar && !hintedAvatar.includes('/_matrix/')) {
      applyAvatar(hintedAvatar)
      return
    }

    void (async () => {
      let peerMxid: string | undefined
      try {
        const matrixClient = await startMatrixClient()
        peerMxid = sourceChat.matrix_peer_id?.trim()

        if (!peerMxid && sourceChat.matrix_room_id) {
          const room = matrixClient.getRoom(sourceChat.matrix_room_id)
          if (room) {
            peerMxid = getMatrixPeerId(room, matrixClient.getUserId())?.trim()
          }
        }

        if (!peerMxid) {
          logDialogProfile('syncDialogPeerAvatar · skipped', {
            chatId,
            reason: 'matrix_peer_id отсутствует',
            matrix_room_id: sourceChat.matrix_room_id ?? null,
            chatIcon: sourceChat.icon ?? null,
          })
          return
        }

        const room = sourceChat.matrix_room_id
          ? matrixClient.getRoom(sourceChat.matrix_room_id) ?? undefined
          : undefined
        const profile = await resolveMatrixSenderProfile(matrixClient, peerMxid, room)

        logDialogProfile('syncDialogPeerAvatar · matrix profile', {
          chatId,
          userId,
          peerMxid,
          matrix_room_id: sourceChat.matrix_room_id ?? null,
          chatIcon: sourceChat.icon ?? null,
          matrixProfile: {
            displayName: profile.displayName,
            avatarUrl: profile.avatarUrl,
          },
          roomMemberAvatar: room?.getMember(peerMxid)?.getMxcAvatarUrl?.() ?? null,
          activeChatStillMatches: this.activeChat?.chat?.id === chatId,
        })

        const avatarUrl = profile.avatarUrl
            || await resolveDialogPeerAvatar(peerMxid, sourceChat.matrix_room_id, sourceChat.icon)

        if (!avatarUrl) {
          return
        }

        applyAvatar(avatarUrl)
      } catch (error) {
        debugConsoleWarn('[chatViewStore] syncDialogPeerAvatar:', error)
        logDialogProfile('syncDialogPeerAvatar · error', {
          chatId,
          peerMxid,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    })()
  },

  syncGroupRoomAvatar(
    chatId: number | string,
    userId: number | string,
    sourceChat: IChat,
    transformedChat: ChatOut,
  ): void {
    if (!isGroupLikeChat(transformedChat)) {
      return
    }

    const matrixRoomId = sourceChat.matrix_room_id
    const existingIcon = transformedChat.icon || sourceChat.icon

    if (!matrixRoomId || existingIcon) {
      return
    }

    void getMatrixRoomAvatarUrl(matrixRoomId).then(avatarUrl => {
      if (!avatarUrl || this.activeChat?.chat?.id !== chatId) {
        return
      }

      const currentAvatar = this.activeChat.contact?.avatar
          || (isGroupLikeChat(this.activeChat.chat) ? this.activeChat.chat.icon : null)
      const stableAvatar = preferStableAvatar(currentAvatar, avatarUrl) || avatarUrl
      if (currentAvatar && isSameAvatarAsset(currentAvatar, stableAvatar)) {
        return
      }

      const activeChat = this.activeChat.chat
      if (isGroupLikeChat(activeChat)) {
        activeChat.icon = stableAvatar
      }

      if (this.activeChat.contact) {
        this.activeChat.contact.avatar = stableAvatar
      }

      const contactIndex = this.chatsContacts.findIndex(c => c.id === userId)
      if (contactIndex !== -1) {
        const existingContact = this.chatsContacts[contactIndex]
        this.chatsContacts[contactIndex] = {
          ...existingContact,
          avatar: preferStableAvatar(existingContact.avatar, stableAvatar) || stableAvatar,
          chat: existingContact.chat && isGroupLikeChat(existingContact.chat)
            ? {
                ...existingContact.chat,
                icon: preferStableAvatar(
                  'icon' in existingContact.chat ? existingContact.chat.icon : null,
                  stableAvatar,
                ) || stableAvatar,
              }
            : existingContact.chat,
        }
      }

      const chatStore = useChatStoreCore()
      const coreIndex = chatStore.chats.findIndex(chat => chat.id === chatId)
      if (coreIndex !== -1 && !chatStore.chats[coreIndex].icon) {
        chatStore.chats[coreIndex] = {
          ...chatStore.chats[coreIndex],
          icon: stableAvatar,
        }
      }
    })
  },

  findContactById(userId: number | string): ChatContact | undefined {
    return (
      this.chatsContacts.find(c => c.id === userId)
        || this.contacts.find(c => c.id === userId)
    )
  },

})
